import moment from 'moment'
import { findIndex, head, includes, propEq, reject, update } from 'ramda'
import { useTranslation, Trans } from 'react-i18next'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { Button } from '@/components/button'
import { confirm } from '@/components/dialog'
import { Input } from '@/components/form'
import Icon from '@/components/icon'
import { breakpoints, colors } from '@/styles'
import formatAddendumPreview from '@/utils/format-addendum-preview'
import { Label, ParagraphSmall, ParagraphMedium } from '@/components/text'
import { Box } from '@/components/structure'
import { CardWrapper } from './buttons'
import { useAddendumModal } from './context'
import EditAddendum from './edit-addendum'
import formatAddendum from '../format-addendum'

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`

const ContentInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 685px;
  width: 100%;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    width: unset;
  }
`

const SelectWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 14px;
  width: 100%;
  margin-bottom: 15px;
`

const SelectText = styled.span`
  color: #142d41;
`

const CheckBox = styled(Input)``

const DashboardGrid = styled.div`
  display: grid;
  grid-gap: 16px;
  grid-template-columns: repeat(2, 336px);
  width: 100%;
  margin-bottom: 50px;

  @media screen and (max-width: ${breakpoints.tabletLandscape}) {
    grid-template-columns: minmax(min(0px, 100%), 336px);
    justify-content: center;
    justify-items: center;
  }
`

const CardTitleWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0 4px;
  svg {
    flex-shrink: 0;
  }
`

const HiddenCardTitleWrapper = styled(CardTitleWrapper)`
  visibility: hidden;
`

const OuterCardTitleWrapper = styled(CardTitleWrapper)`
  position: absolute;
  top: 0;
  padding: 16px 16px 0;
  visibility: visible;
`

const CardDescription = styled(ParagraphSmall)`
  color: ${colors.grey56};
  margin: 0;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

const CreateCardWrapper = styled(CardWrapper)`
  height: unset;
  align-self: start;
  min-height: unset;

  svg {
    * {
      fill: ${colors.link};
    }
  }
`

const CreateText = styled.span`
  color: ${colors.link};
`

const StyledButton = styled(Button)`
  width: 100%;
  @media screen and (max-width: ${breakpoints.phoneMax}) {
    position: sticky;
    bottom: 10px;
  }
`

const TitleWrapper = styled.span`
  flex: 1;
  user-select: none;
`

const UpdateWrapper = styled.div`
  padding-top: 8px;
  flex: 1;
  display: flex;
  align-items: flex-end;
`

const Spacer = styled.div`
  height: 26px;
  width: 26px;
  min-width: 26px;
  border: 1px solid rgba(0, 0, 0, 0);
`

const Card = ({
  template,
  templates,
  setTemplates,
  selected,
  addSelection,
  removeSelection,
  isCustomList
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { addStep, backStep, variables } = useAddendumModal()
  const checked = includes(template, selected)

  const handleUpdateCustomTemplate = async addendum => {
    const response = await dispatch.contract.editAddendumTemplate({
      addendumId: addendum.listing_contract_addendum_template_id,
      title: addendum.title,
      content: addendum.content
    })
    if (response?.response?.ok) {
      const formattedAddendum = head(formatAddendum([response.body]))
      const updatedTemplates = update(
        findIndex(
          propEq(
            'listing_contract_addendum_template_id',
            formattedAddendum.listing_contract_addendum_template_id
          ),
          templates
        ),
        formattedAddendum,
        templates
      )
      setTemplates(updatedTemplates)
    }
  }

  const handleUpdateStandardTemplate = addendum => {
    const updatedTemplates = update(
      findIndex(propEq('title', template.title), templates),
      addendum,
      templates
    )
    setTemplates(updatedTemplates)
  }

  const handleDeleteCustomTemplate = addendum => {
    return confirm(
      t('l.contract.edit.confirm_delete_addendum.title', 'Delete Template'),
      t(
        'l.contract.edit.confirm_delete_addendum.text',
        'Are you sure you want to delete this custom addendum template from your company folder? All landlords will lose access to this template.'
      ),
      {
        okLabel: t('common.delete', 'Delete'),
        cancelLabel: t('common.cancel', 'Cancel'),
        isWarning: true
      }
    ).then(async () => {
      const response = await dispatch.contract.deleteAddendumTemplate({
        addendumId: addendum.listing_contract_addendum_template_id
      })
      if (response?.response?.ok) {
        const updatedTemplates = reject(
          propEq('listing_contract_addendum_template_id', response.body.id),
          templates
        )
        setTemplates(updatedTemplates)
        backStep()
      }
    })
  }

  const editTemplate = {
    component: EditAddendum,
    addendumType: isCustomList ? 'company' : 'standard',
    addendum: template,
    titleText: t(
      'l.contract.edit.edit_company_template_addendum_modal.title',
      'Edit Company Addendum'
    ),
    footnote: t(
      'l.contract.edit.edit_addendum_update_custom_info.text',
      'Please note: Any changes you make will be saved to your company template for all landlords to use.'
    ),
    buttonText: t('l.contract.edit.update_template.button', 'Update Template'),
    onSubmit: isCustomList ? handleUpdateCustomTemplate : handleUpdateStandardTemplate,
    onSubmitComplete: isCustomList ? 'view' : 'back',
    onDelete: handleDeleteCustomTemplate,
    onDeleteComplete: 'back'
  }

  return (
    <Wrapper>
      <CardWrapper
        type="button"
        onClick={() => {
          addStep(editTemplate)
        }}
      >
        <HiddenCardTitleWrapper aria-hidden>
          <TitleWrapper>
            <Label type="large" as="span">
              {template.title}
            </Label>
          </TitleWrapper>
          <Spacer />
        </HiddenCardTitleWrapper>
        <CardDescription>{formatAddendumPreview(template, t, variables)}</CardDescription>
        {template.last_modification_at && (
          <UpdateWrapper>
            <Label type="small" fontSize="12px">
              {isCustomList ? (
                <Trans
                  i18nKey="l.contract.edit.addendum_date_modified.text"
                  values={{
                    date: moment(template.last_modification_at).format('MM/DD/YY')
                  }}
                  defaults="Date modified: {{ date }}"
                />
              ) : (
                <Trans
                  i18nKey="l.contract.edit.addendum_date_updated.text"
                  values={{
                    date: moment(template.last_modification_at).format('MM/DD/YY')
                  }}
                  defaults="Date updated: {{ date }}"
                />
              )}
            </Label>
          </UpdateWrapper>
        )}
      </CardWrapper>
      <OuterCardTitleWrapper as="label">
        <TitleWrapper>
          <Label type="large" as="span">
            {template.title}
          </Label>
        </TitleWrapper>
        <CheckBox
          onClick={e => e.stopPropagation()}
          onChange={e => (e.target.checked ? addSelection(template) : removeSelection(template))}
          checked={checked}
          type="checkbox"
        />
      </OuterCardTitleWrapper>
    </Wrapper>
  )
}

const ListBase = ({
  description,
  isCustomList,
  templates,
  setTemplates,
  selected,
  setSelected
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { ModalFooter, close, addStep, isAdmin, addContractAddendums } = useAddendumModal()

  const addSelection = a => setSelected(arr => [...arr, a])
  const removeSelection = a =>
    setSelected(arr =>
      reject(
        propEq('listing_contract_addendum_template_id', a.listing_contract_addendum_template_id),
        arr
      )
    )

  const allSelected = selected.length === templates.length

  const handleAddToContract = () => {
    addContractAddendums(selected)
    close()
  }

  const handleAddCustomAddendum = async addendum => {
    const response = await dispatch.contract.createAddendumTemplate({
      title: addendum.title,
      content: addendum.content
    })
    if (response?.response?.ok) {
      const formattedAddendum = head(formatAddendum([response.body]))
      setTemplates(arr => [...arr, formattedAddendum])
    }
  }

  const createCustomAddendum = {
    component: EditAddendum,
    addendumType: 'company',
    isEditing: true,
    titleText: t(
      'l.contract.edit.create_reusable_addendum_modal.title',
      'Create Reusable Addendum'
    ),
    footnote: t(
      'l.contract.edit.edit_addendum_create_custom_info.text',
      'Please note: This will be saved to your company template folder for all landlords in your company to access.'
    ),
    buttonText: t('l.contract.edit.create_template.button', 'Create Template'),
    onSubmit: handleAddCustomAddendum,
    onSubmitComplete: 'back'
  }

  return (
    <ContentInner>
      {description ? (
        <Box textAlign="center" bottom={24}>
          <ParagraphMedium>{description}</ParagraphMedium>
        </Box>
      ) : (
        <></>
      )}
      <SelectWrapper as="label">
        <SelectText>
          <Label as="span">
            {t('l.contract.edit.select_all.label', 'Select All')} {`(${templates.length})`}
          </Label>
        </SelectText>
        <CheckBox
          onChange={e => {
            e.target.checked ? setSelected(templates) : setSelected([])
          }}
          checked={allSelected}
          type="checkbox"
        />
      </SelectWrapper>
      <DashboardGrid>
        <>
          {templates.map(template => {
            return (
              <Card
                key={template.listing_contract_addendum_template_id}
                template={template}
                templates={templates}
                setTemplates={setTemplates}
                selected={selected}
                addSelection={addSelection}
                removeSelection={removeSelection}
                isCustomList={isCustomList}
              />
            )
          })}
          {isCustomList && isAdmin && (
            <CreateCardWrapper onClick={() => addStep(createCustomAddendum)}>
              <CardTitleWrapper>
                <CreateText>
                  <Label fontSize="14px">
                    {t('l.contract.edit.create_custom_addendum.button', 'Create Custom Addendum')}
                  </Label>
                </CreateText>
                <Icon id="plus_small" width="24" height="24" />
              </CardTitleWrapper>
            </CreateCardWrapper>
          )}
        </>
      </DashboardGrid>
      <ModalFooter>
        <StyledButton theme="primary" onClick={handleAddToContract}>
          {t('l.contract.edit.add_to_contract.button', 'Add to Contract')}
        </StyledButton>
      </ModalFooter>
    </ContentInner>
  )
}

export default ListBase
